import React from "react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

interface DescriptionFieldProps {
  description: string;
  setDescription: (description: string) => void;
  serviceName: string;
  categoryName: string;
}

export const DescriptionField = ({
  description,
  setDescription,
  serviceName,
  categoryName,
}: DescriptionFieldProps) => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [isGeneratingDescription, setIsGeneratingDescription] = React.useState(false);

  const generateDescription = async () => {
    if (!serviceName) {
      toast({
        title: t('services.serviceNameRequiredTitle'),
        description: t('services.serviceNameRequiredDescription'),
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingDescription(true);

    try {
      const { data, error } = await supabase.functions.invoke('generate-service-description', {
        body: { 
          serviceName,
          category: categoryName,
          locale: i18n.language
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data.description) {
        setDescription(data.description);
        toast({
          title: t('services.descriptionGenerated'),
          description: t('services.descriptionGeneratedDescription'),
        });
      } else {
        throw new Error("Failed to generate description");
      }
    } catch (error) {
      console.error('Error generating description:', error);
      toast({
        title: t('services.generationFailed'),
        description: t('services.generationFailedDescription'),
        variant: "destructive",
      });
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center">
        <Label htmlFor="description">{t('services.descriptionRequired')}</Label>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="flex items-center gap-1.5 text-xs bg-gradient-to-r from-gebeya-pink/10 to-gebeya-orange/10 border-gebeya-pink/20 hover:bg-gradient-to-r hover:from-gebeya-pink/20 hover:to-gebeya-orange/20"
          onClick={generateDescription}
          disabled={isGeneratingDescription || !serviceName}
        >
          {isGeneratingDescription ? (
            <>
              <img 
                src="/lovable-uploads/f8714beb-a432-4da6-acab-3ef6f0a42367.png" 
                alt="Generating" 
                className="h-3.5 w-3.5 gebeya-spinner" 
              />
              <span className="text-gebeya-pink">{t('services.generating')}</span>
            </>
          ) : (
            <>
              <Sparkles className="h-3.5 w-3.5 text-gebeya-pink" />
              <span className="text-gebeya-pink">{t('services.generateWithAI')}</span>
            </>
          )}
        </Button>
      </div>
      <Textarea
        id="description"
        placeholder={t('services.descriptionPlaceholder')}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        className="mt-1 h-32"
      />
    </div>
  );
};
